import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { uploadFile, postData, getData, deleteData } from '../../../webService/webservice.js';
import { Typography } from "@mui/material";
import "../../../styles/Auth/store.css";
import TVFMLoader from "../Loader/loader.jsx";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import { Icon } from "@iconify/react";
import PackageInvoiceModal from "../../../components/Subscription/PackageInvoiceModal.jsx";

const calculateExpiryDatesClient = (startDateInput, baseValidity, validityUnit, extraValidity = 0, extraValidityUnit = 'Days') => {
    const startDate = new Date(startDateInput || Date.now());

    const baseExpiryDate = new Date(startDate);
    const baseVal = Number(baseValidity) || 0;
    if (validityUnit === 'Days') {
        baseExpiryDate.setDate(baseExpiryDate.getDate() + baseVal);
    } else if (validityUnit === 'Months') {
        baseExpiryDate.setMonth(baseExpiryDate.getMonth() + baseVal);
    } else if (validityUnit === 'Years') {
        baseExpiryDate.setFullYear(baseExpiryDate.getFullYear() + baseVal);
    }

    const finalExpiryDate = new Date(baseExpiryDate);
    const extraVal = Number(extraValidity) || 0;
    if (extraVal > 0) {
        if (extraValidityUnit === 'Days') {
            finalExpiryDate.setDate(finalExpiryDate.getDate() + extraVal);
        } else if (extraValidityUnit === 'Months') {
            finalExpiryDate.setMonth(finalExpiryDate.getMonth() + extraVal);
        }
    }

    return {
        startDateStr: startDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        baseExpiryStr: baseExpiryDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        finalExpiryStr: finalExpiryDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    };
};

const AddStoreForm = ({ onSuccess, onCancel }) => {
    const navigate = useNavigate();
    const [showLoader, setShowLoader] = useState(false);
    const [activePackages, setActivePackages] = useState([]);
    const [selectedPackageDetails, setSelectedPackageDetails] = useState(null);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [createdInvoiceData, setCreatedInvoiceData] = useState(null);
    const [createdSubId, setCreatedSubId] = useState(null);
    const [documents, setDocuments] = useState([
        { documentType: "", documentName: "", files: [] }
    ]);
    const [errors, setErrors] = useState({});
    const [selectedFranchise, setSelectedFranchise] = useState(null);
    const [assignPopup, setAssignPopup] = useState(false);
    const [assignLoading, setAssignLoading] = useState(false);
    const [storeImage, setStoreImage] = useState(null);
    const [storeLogo, setStoreLogo] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
    const [formData, setFormData] = useState({
        storeName: '',
        branchName: '',
        phoneNumber: '',
        emailAddress: '',
        address: '',
        postalCode: '',
        state: '',
        country: '',
        location: '',
        documents: [],
        employees: [],
        brandName: "",
        employeeNames: {},
        clusterManager: "",
        clusterManagerName: "",
        gstData: {
            gstNumber: '',
            valid: false,
            status: 'PENDING'
        },
        storeAdmin: "",
        isFranchiseAvailable: false,
        franchiseId: "",
        franchiseName: "",
        packageId: "",
        storeImageObject: "",
        storeLogoImageObject: "",
    });

    React.useEffect(() => {
        const fetchActivePackages = async () => {
            try {
                const res = await getData("packages", { status: "Active", limit: 100 });
                if (res && (res.status === 200 || res.success) && res.data) {
                    setActivePackages(res.data.packages || []);
                }
            } catch (err) {
                console.error("Error fetching active packages for store creation:", err);
            }
        };
        fetchActivePackages();
    }, []);

    const indianStates = [
        "Andhra Pradesh",
        "Arunachal Pradesh",
        "Assam",
        "Bihar",
        "Chhattisgarh",
        "Goa",
        "Gujarat",
        "Haryana",
        "Himachal Pradesh",
        "Jharkhand",
        "Karnataka",
        "Kerala",
        "Madhya Pradesh",
        "Maharashtra",
        "Manipur",
        "Meghalaya",
        "Mizoram",
        "Nagaland",
        "Odisha",
        "Punjab",
        "Rajasthan",
        "Sikkim",
        "Tamil Nadu",
        "Telangana",
        "Tripura",
        "Uttar Pradesh",
        "Uttarakhand",
        "West Bengal",
        "Andaman and Nicobar Islands",
        "Chandigarh",
        "Dadra & Nagar Haveli and Daman & Diu",
        "Delhi",
        "Jammu & Kashmir",
        "Ladakh",
        "Lakshadweep",
        "Puducherry"
    ];

    const validateStoreForm = () => {
        let newErrors = {};
        let valid = true;

        // Basic fields
        if (!formData.branchName?.trim()) {
            newErrors.branchName = "Branch Name is required";
            valid = false;
        }
        if (!formData.emailAddress?.trim()) {
            newErrors.emailAddress = "Store Email Address is required";
            valid = false;
        } else if (
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)
        ) {
            newErrors.emailAddress = "Enter a valid email address";
            valid = false;
        }
        if (!formData.phoneNumber || formData.phoneNumber.length !== 10) {
            newErrors.phoneNumber = "Phone Number must be 10 digits";
            valid = false;
        }
        if (!formData.address?.trim()) {
            newErrors.address = "Address is required";
            valid = false;
        }
        if (!formData.postalCode?.trim()) {
            newErrors.postalCode = "Postal Code is required";
            valid = false;
        } else if (!/^\d{6}$/.test(formData.postalCode)) {
            newErrors.postalCode = "Postal Code must be 6 digits";
            valid = false;
        }
        if (!formData.state?.trim()) {
            newErrors.state = "State is required";
            valid = false;
        }

        if (!formData.location?.trim()) {
            newErrors.location = "Location is required";
            valid = false;
        }

        if (!formData.country?.trim()) {
            newErrors.country = "Country is required";
            valid = false;
        }



        setErrors(newErrors);
        return valid;
    };

    const addDocumentRow = () => {
        if (documents.length >= 10) {
            alert("You can upload a maximum of 10 document entries.");
            return;
        }
        setDocuments(prev => [
            ...prev,
            { documentType: "", documentName: "", files: [] }
        ]);
    };

    const removeDocumentRow = (index) => {
        setDocuments(prev => prev.filter((_, i) => i !== index));
    };

    const updateDocumentRow = (index, field, value) => {
        const updated = [...documents];
        updated[index][field] = value;
        setDocuments(updated);
    };

    const handleDocumentFiles = (index, fileList) => {
        const files = Array.from(fileList);
        const validFiles = files.filter(f => f.size <= 5 * 1024 * 1024);
        const invalidFiles = files.filter(f => f.size > 5 * 1024 * 1024);

        if (invalidFiles.length > 0) {
            alert("Some files exceed 5 MB limit. They were ignored.");
        }

        const updated = [...documents];
        updated[index].files = validFiles;
        setDocuments(updated);
    };


    const handleAddStore = async (e) => {
        e.preventDefault();

        if (!validateStoreForm()) {
            return;
        }

        setErrors({});
        setShowLoader(true);
        try {
            const token = localStorage.getItem("Token");
            const userId = localStorage.getItem("userId");

            let storeImageObject = null;
            let storeLogoImageObject = null;

            if (storeImage) {
                const uploadResult = await uploadFile(
                    `files/upload/${userId}`,
                    storeImage
                );

                if (uploadResult?.status === 200) {
                    storeImageObject = uploadResult.data;
                } else {
                    toast.error("Image upload failed");
                    setShowLoader(false);
                    return;
                }
            }

            if (storeLogo) {
                const uploadResult = await uploadFile(
                    `files/upload/${userId}`,
                    storeLogo
                );

                if (uploadResult?.status === 200) {
                    storeLogoImageObject = uploadResult.data;
                } else {
                    toast.error("Logo upload failed");
                    setShowLoader(false);
                    return;
                }
            }
            const storeData = {
                storeName: formData.branchName || formData.storeName,
                branchName: formData.branchName,
                phoneNumber: formData.phoneNumber,
                emailAddress: formData.emailAddress,
                address: formData.address,
                postalCode: formData.postalCode,
                state: formData.state,
                country: formData.country,
                location: formData.location,

                brandName: formData.brandName,
                employees: formData.employees || [],
                clusterManager: formData.clusterManager || null,
                storeAdmin: formData.storeAdmin || null,
                isFranchiseAvailable: formData.isFranchiseAvailable,
                franchiseId: formData.franchiseId || null,
                packageId: formData.packageId || null,
                gstData: {
                    gstNumber: formData.gstData?.gstNumber,
                    valid: formData.gstData?.valid,
                    status: formData.gstData?.status,
                },
                ...(storeImageObject && { storeImageObject }),
                ...(storeLogoImageObject && { storeLogoImageObject }),
                documents: [],
            };

            // --------------------------
            //  DOCUMENT UPLOAD LOGIC
            // --------------------------
            for (let i = 0; i < documents.length; i++) {
                const doc = documents[i];

                if (!doc.files || doc.files.length === 0) continue;

                let uploadedFiles = [];

                for (let file of doc.files) {
                    const uploadRes = await uploadFile(`files/upload/store-docs`, file);

                    if (uploadRes?.status === 200) {
                        uploadedFiles.push(uploadRes.data);
                    } else {
                        toast.error("Document upload failed for " + file.name);
                    }
                }

                storeData.documents.push({
                    documentType: doc.documentType,
                    documentName: doc.documentType === "OTHERS" ? doc.documentName : "",
                    files: uploadedFiles,
                });
            }

            console.log("FINAL STORE PAYLOAD →", storeData);

            const response = await postData("stores/", storeData, token);

            const invData = response?.data?.packageInvoice || response?.packageInvoice;
            const subId = response?.data?.subscriptionDetails?._id || response?.data?.subscriptionDetails?.id || response?.data?.purchaseDetails?._id;
            const emailSent = response?.data?.emailSent ?? response?.emailSent;
            const emailMsg = response?.data?.emailMessage ?? response?.emailMessage;

            if (emailSent === false && emailMsg) {
                toast.warning(emailMsg);
            } else {
                toast.success(response.message || "Store added successfully");
            }

            // If a package was assigned, prepare invoice print option
            if (invData || subId || storeData.packageId) {
                setCreatedInvoiceData(invData || null);
                setCreatedSubId(subId || null);
                setShowInvoiceModal(true);
            }

            // ✅ RESET FORM
            setFormData({
                storeName: "",
                branchName: "",
                phoneNumber: "",
                emailAddress: "",
                address: "",
                postalCode: "",
                brandName: "",
                state: "",
                country: "",
                location: "",
                employees: [],
                storeImageObject: null,
                storeLogoImageObject: null,
                clusterManager: "",
                franchisorId: "",
                storeAdmin: "",
                gstData: { gstNumber: "", valid: false, status: "PENDING" },
            });

            setStoreLogo(null);
            setLogoPreviewUrl(null);
            setStoreImage(null);
            setPreviewUrl(null);

            setDocuments([{ documentType: "", documentName: "", files: [] }]);

            if (!invData && !subId && !storeData.packageId) {
                if (onSuccess) {
                    onSuccess();
                } else {
                    navigate("/stores");
                }
            }

        } catch (error) {
            console.error("Add Store Error:", error);

            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to add store";

            toast.error(errorMessage);
        } finally {
            setTimeout(() => {
                setShowLoader(false);
            }, 1000);
        }
    };

    const loadFranchises = async (search, loadedOptions, { page }) => {
        try {
            const sessionToken = localStorage.getItem("Token");

            const res = await getData(
                "franchise/franchises",
                {
                    page,
                    limit: 100000,
                    search: search || "",
                },
                sessionToken
            );

            const results = res?.data?.results || [];
            const hasMore = results.length === 10;

            const options = results.map((f) => ({
                value: f._id,
                label: f.franchiseName,
            }));

            return {
                options,
                hasMore,
                additional: { page: page + 1 },
            };
        } catch (error) {
            console.error("Failed to load franchises", error);
            return {
                options: [],
                hasMore: false,
                additional: { page },
            };
        }
    };

    return (
        <div className="add-Store-form" style={{ maxWidth: '100%', margin: '0 auto' }}>
            {showLoader && (
                <div className="scissor-overlay">
                    <div className="scissor-loader">
                        <TVFMLoader />
                    </div>
                </div>
            )}
            <Typography variant="h6" mb={2}>Add Store</Typography>
            <form className="Store-form" onSubmit={handleAddStore}>
                <div className="Store-form-row">
                    <div className="Store-form-group">
                        <label className="input-label">Franchise Available?</label>

                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={formData.isFranchiseAvailable}
                                onChange={(e) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        isFranchiseAvailable: e.target.checked
                                    }));

                                    if (!e.target.checked) {
                                        setSelectedFranchise(null);
                                        setFormData(prev => ({
                                            ...prev,
                                            franchiseId: "",
                                            franchiseName: ""
                                        }));
                                    }
                                }}
                            />
                            <span className="slider round"></span>
                        </label>
                    </div>

                    {formData.isFranchiseAvailable && (
                        <div className="Store-form-group">
                            <label className="input-label">Franchise</label>

                            <AsyncPaginate
                                value={selectedFranchise}
                                loadOptions={loadFranchises}
                                onChange={(value) => {
                                    setSelectedFranchise(value);
                                    setFormData(prev => ({
                                        ...prev,
                                        franchiseId: value?.value || "",
                                        franchiseName: value?.label || ""
                                    }));
                                }}
                                additional={{ page: 1 }}
                                placeholder="Select Franchise"
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 1050 }),
                                    control: (base) => ({
                                        ...base,
                                        borderRadius: '8px',
                                        borderColor: '#e2e8f0',
                                        minHeight: '42px',
                                        boxShadow: 'none',
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        backgroundColor: state.isSelected
                                            ? '#3C0008'
                                            : state.isFocused
                                                ? '#f8e8ea'
                                                : 'white',
                                        color: state.isSelected ? 'white' : '#2d2d2d',
                                        cursor: 'pointer',
                                    }),
                                }}
                            />
                        </div>
                    )}


                    <div className="Store-form-group">
                        <label htmlFor="storeName" className="input-label">
                            Store Name<span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="Enter Store name"
                            className="store-input"
                            value={formData.branchName || ""}
                            onChange={e =>
                                setFormData(prev => ({ ...prev, branchName: e.target.value }))
                            }
                        />
                        {errors.branchName && <p className="field-error">{errors.branchName}</p>}
                    </div>
                    <div className="Store-form-group">
                        <label htmlFor="emailAddress" className="input-label">
                            Store Email Address<span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="Enter Store Email Address"
                            className="store-input"
                            value={formData.emailAddress || ""}
                            onChange={e =>
                                setFormData(prev => ({ ...prev, emailAddress: e.target.value }))
                            }
                        />
                        {errors.emailAddress && <p className="field-error">{errors.emailAddress}</p>}
                    </div>
                </div>
                <div className="Store-form-row">
                    <div className="Store-form-group">
                        <label htmlFor="brandName" className="input-label">
                            Brand Name
                        </label>
                        <input
                            type="text"
                            placeholder="Enter Brand Name"
                            className="store-input"
                            value={formData.brandName || ""}
                            onChange={e =>
                                setFormData(prev => ({ ...prev, brandName: e.target.value }))
                            }
                        />
                    </div>
                    <div className="Store-form-group">
                        <label htmlFor="phoneNumber" className="input-label">
                            Store Phone Number<span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="Enter Store Phone Number"
                            className="store-input"
                            maxLength={10}
                            value={formData.phoneNumber || ""}
                            title="Mobile Number must be exactly 10 digits"
                            onChange={(e) => {
                                const value = e.target.value;
                                // Allow only digits and max 10 characters
                                if (/^\d{0,10}$/.test(value)) {
                                    setFormData((prev) => ({ ...prev, phoneNumber: value }));
                                }
                            }}
                        />
                        {errors.phoneNumber && <p className="field-error">{errors.phoneNumber}</p>}
                    </div>
                    <div className="Store-form-group">
                        <label htmlFor="address" className="input-label">
                            Address<span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="Enter Store address"
                            className="store-input"
                            value={formData.address || ""}
                            onChange={e =>
                                setFormData(prev => ({ ...prev, address: e.target.value }))
                            }
                        />
                        {errors.address && <p className="field-error">{errors.address}</p>}
                    </div>
                </div>
                <div className="Store-form-row">
                    <div className="Store-form-group">
                        <label htmlFor="state" className="input-label">
                            State<span className="required">*</span>
                        </label>
                        <Select
                            options={indianStates.map(s => ({ value: s, label: s }))}
                            value={formData.state ? { value: formData.state, label: formData.state } : null}
                            onChange={(opt) => setFormData(prev => ({ ...prev, state: opt?.value || "" }))}
                            placeholder="Select State"
                            isClearable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 1050 }),
                                control: (base) => ({
                                    ...base,
                                    borderRadius: '8px',
                                    borderColor: '#e2e8f0',
                                    minHeight: '42px',
                                    boxShadow: 'none',
                                }),
                                option: (base, state) => ({
                                    ...base,
                                    backgroundColor: state.isSelected ? '#3C0008' : state.isFocused ? '#f8e8ea' : 'white',
                                    color: state.isSelected ? 'white' : '#2d2d2d',
                                    cursor: 'pointer',
                                }),
                            }}
                        />
                        {errors.state && <p className="field-error">{errors.state}</p>}
                    </div>
                    <div className="Store-form-group">
                        <label htmlFor="postalCode" className="input-label">
                            Postal Code<span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="Enter Store Postal Code"
                            className="store-input"
                            value={formData.postalCode || ""}
                            onChange={e =>
                                setFormData(prev => ({ ...prev, postalCode: e.target.value }))
                            }
                        />
                        {errors.postalCode && <p className="field-error">{errors.postalCode}</p>}
                    </div>
                </div>

                <div className="Store-form-row">
                    <div className="Store-form-group">
                        <label htmlFor="country" className="input-label">
                            Country<span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="Enter Country"
                            className="store-input"
                            value={formData.country}
                            onChange={e =>
                                setFormData(prev => ({ ...prev, country: e.target.value }))
                            }
                        />
                        {errors.country && <p className="field-error">{errors.country}</p>}
                    </div>

                    <div className="Store-form-group">
                        <label htmlFor="location" className="input-label">
                            Location<span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            placeholder="Enter Area / Locality"
                            className="store-input"
                            value={formData.location || ""}
                            onChange={e =>
                                setFormData(prev => ({ ...prev, location: e.target.value }))
                            }
                        />
                        {errors.location && <p className="field-error">{errors.location}</p>}
                    </div>
                </div>


                <div className="Store-form-row">
                    {/* GST Number */}
                    <div className="Store-form-group">
                        <label className="input-label">
                            GST Number
                        </label>
                        <input
                            type="text"
                            className="store-input"
                            value={formData.gstData?.gstNumber || ""}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    gstData: {
                                        ...prev.gstData,
                                        gstNumber: e.target.value.toUpperCase(),
                                    },
                                }))
                            }
                        />

                    </div>

                    {/* GST Status */}
                    <div className="Store-form-group">
                        <label className="input-label">GST Status</label>
                        <select
                            className="store-input"
                            value={formData.gstData?.status || "PENDING"}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    gstData: {
                                        ...prev.gstData,
                                        status: e.target.value,
                                    },
                                }))
                            }
                        >
                            <option value="PENDING">PENDING</option>
                            <option value="VERIFIED">VERIFIED</option>
                            <option value="REJECTED">REJECTED</option>
                        </select>

                    </div>
                </div>

                {/* 📦 PACKAGE SELECTION SECTION */}
                <div className="Store-form-row" style={{ flexDirection: 'column', gap: '12px', marginTop: '16px', background: '#f8fafc', padding: '18px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon icon="mdi:package-variant-closed" width="22" height="22" color="#3C0008" />
                        <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>
                            Subscription Package Assignment
                        </h4>
                    </div>

                    <div className="Store-form-group" style={{ width: '100%' }}>
                        <label className="input-label">
                            Select Active Package
                        </label>
                        <select
                            className="store-input"
                            value={formData.packageId || ""}
                            onChange={(e) => {
                                const pkgId = e.target.value;
                                setFormData(prev => ({ ...prev, packageId: pkgId }));
                                const found = activePackages.find(p => p._id === pkgId);
                                setSelectedPackageDetails(found || null);
                            }}
                        >
                            <option value="">-- Select Package --</option>
                            {activePackages.map(pkg => (
                                <option key={pkg._id} value={pkg._id}>
                                    {pkg.packageName} (₹{pkg.packagePrice} - {pkg.totalValidity})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Read-only Package Details & Expiry Calculation Snapshot */}
                    {selectedPackageDetails && (() => {
                        const dates = calculateExpiryDatesClient(
                            new Date(),
                            selectedPackageDetails.baseValidity,
                            selectedPackageDetails.validityUnit,
                            selectedPackageDetails.extraValidity,
                            selectedPackageDetails.extraValidityUnit
                        );

                        const price = Number(selectedPackageDetails.packagePrice) || 0;
                        const gst = Number(selectedPackageDetails.gstPercentage) || 0;
                        let gstAmount = 0;
                        let totalAmount = price;

                        if (selectedPackageDetails.isGstApplicable && gst > 0) {
                            if (selectedPackageDetails.gstType === 'Exclusive') {
                                gstAmount = (price * gst) / 100;
                                totalAmount = price + gstAmount;
                            } else if (selectedPackageDetails.gstType === 'Inclusive') {
                                gstAmount = price - (price / (1 + gst / 100));
                                totalAmount = price;
                            }
                        }

                        const displayGstAmt = selectedPackageDetails.gstAmount !== undefined ? selectedPackageDetails.gstAmount : Number(gstAmount.toFixed(2));
                        const displayTotalAmt = selectedPackageDetails.totalAmount !== undefined ? selectedPackageDetails.totalAmount : Number(totalAmount.toFixed(2));

                        return (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(5, 1fr)',
                                gap: '14px 16px',
                                width: '100%',
                                background: '#ffffff',
                                padding: '16px',
                                borderRadius: '12px',
                                border: '1px solid #cbd5e1',
                                marginTop: '8px'
                            }}>
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Package Price</span>
                                    <strong style={{ fontSize: '15px', color: '#3C0008' }}>₹{Number(selectedPackageDetails.packagePrice).toLocaleString('en-IN')}</strong>
                                </div>
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>GST Details</span>
                                    <strong style={{ fontSize: '13.5px', color: '#1e293b' }}>
                                        {selectedPackageDetails.isGstApplicable ? `${selectedPackageDetails.gstPercentage}% (${selectedPackageDetails.gstType})` : 'No GST'}
                                    </strong>
                                </div>
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>GST Amount</span>
                                    <strong style={{ fontSize: '13.5px', color: '#475569' }}>₹{Number(displayGstAmt).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
                                </div>
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Total Amount</span>
                                    <strong style={{ fontSize: '14.5px', color: '#166534' }}>₹{Number(displayTotalAmt).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
                                </div>
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Base Validity</span>
                                    <strong style={{ fontSize: '13.5px', color: '#1e293b' }}>{selectedPackageDetails.baseValidity} {selectedPackageDetails.validityUnit}</strong>
                                </div>
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Extra Validity / Offer</span>
                                    <strong style={{ fontSize: '13.5px', color: '#b45309' }}>
                                        {selectedPackageDetails.extraValidity > 0 ? `${selectedPackageDetails.extraValidity} ${selectedPackageDetails.extraValidityUnit}` : 'None'}
                                    </strong>
                                </div>
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Total Validity</span>
                                    <strong style={{ fontSize: '14px', color: '#2563eb' }}>{selectedPackageDetails.totalValidity}</strong>
                                </div>
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Validity Start Date</span>
                                    <strong style={{ fontSize: '13.5px', color: '#16a34a' }}>{dates.startDateStr} (Store Creation Date)</strong>
                                </div>
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Base Expiry Date</span>
                                    <strong style={{ fontSize: '13.5px', color: '#475569' }}>{dates.baseExpiryStr}</strong>
                                </div>
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>Final Expiry Date</span>
                                    <strong style={{ fontSize: '14px', color: '#dc2626' }}>{dates.finalExpiryStr}</strong>
                                </div>
                            </div>
                        );
                    })()}
                </div>
                <div className="Store-form-row">
                    <label className="input-label">Store Documents</label>

                    <div className="document-list">

                        {documents.map((doc, index) => (
                            <div key={index} className="document-row-single">

                                {/* REMOVE BUTTON */}
                                <button
                                    type="button"
                                    className="remove-doc-btn"
                                    onClick={() => removeDocumentRow(index)}
                                >
                                    ✕
                                </button>

                                {/* DOCUMENT TYPE */}
                                <select
                                    className="store-input"
                                    value={doc.documentType}
                                    onChange={(e) =>
                                        updateDocumentRow(index, "documentType", e.target.value)
                                    }
                                >
                                    <option value="">Select Document Type</option>
                                    <option value="RENTAL AGREEMENT">RENTAL AGREEMENT</option>
                                    <option value="OTHERS">OTHERS</option>
                                </select>


                                {/* OTHERS free text input */}
                                {doc.documentType === "OTHERS" && (
                                    <>
                                        <input
                                            type="text"
                                            className="store-input"
                                            placeholder="Enter Document Name"
                                            value={doc.documentName}
                                            onChange={(e) =>
                                                updateDocumentRow(index, "documentName", e.target.value)
                                            }
                                        />
                                    </>
                                )}

                                {/* File Upload */}
                                <input
                                    type="file"
                                    multiple
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    className="store-input"
                                    onChange={(e) => handleDocumentFiles(index, e.target.files)}
                                />

                            </div>
                        ))}

                        {/* ADD NEW ROW BUTTON */}
                        <button
                            type="button"
                            onClick={addDocumentRow}
                            disabled={documents.length >= 10}
                            className="add-row-btn"
                        >
                            + Add Document Row
                        </button>
                    </div>
                </div>

                {/* LAST ROW: Store Image & Store Logo */}
                <div className="Store-form-row" style={{ justifyContent: 'space-around', margin: '20px 0' }}>
                    <div className="Store-form-group" style={{ alignItems: 'center', maxWidth: '300px' }}>
                        <label htmlFor="storeImage" className="input-label" style={{ textAlign: 'center', width: '100%' }}>
                            Store Image
                        </label>
                        <div className="file-input-wrapper" style={{ width: '100%' }}>
                            <input
                                type="file"
                                accept="image/*"
                                id="storeImage"
                                className="Product-input"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];

                                    if (!file) return;

                                    if (file.size > 2 * 1024 * 1024) {
                                        toast.error("The uploaded image size exceeds the maximum allowed limit of 2 MB. Please upload an image smaller than 2 MB.");
                                        e.target.value = "";
                                        setStoreImage(null);
                                        setPreviewUrl(null);
                                        return;
                                    }

                                    setStoreImage(file);
                                    setPreviewUrl(URL.createObjectURL(file));
                                }}
                            />
                        </div>
                        {previewUrl && (
                            <div className="image-preview" style={{ marginTop: '15px', position: 'relative', display: 'inline-block' }}>
                                <img src={previewUrl} alt="Store Preview" style={{ width: '140px', height: '140px', borderRadius: '15px', objectFit: 'cover', border: '2px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStoreImage(null);
                                        setPreviewUrl(null);
                                        const fileInput = document.getElementById("storeImage");
                                        if (fileInput) fileInput.value = "";
                                    }}
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        background: '#C10000',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '24px',
                                        height: '24px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        zIndex: 10
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="Store-form-group" style={{ alignItems: 'center', maxWidth: '300px' }}>
                        <label htmlFor="storeLogo" className="input-label" style={{ textAlign: 'center', width: '100%' }}>
                            Store Logo
                        </label>
                        <div className="file-input-wrapper" style={{ width: '100%' }}>
                            <input
                                type="file"
                                accept="image/*"
                                id="storeLogo"
                                className="Product-input"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];

                                    if (!file) return;

                                    if (file.size > 2 * 1024 * 1024) {
                                        toast.error("The uploaded image size exceeds the maximum allowed limit of 2 MB. Please upload an image smaller than 2 MB.");
                                        e.target.value = "";
                                        setStoreLogo(null);
                                        setLogoPreviewUrl(null);
                                        return;
                                    }

                                    setStoreLogo(file);
                                    setLogoPreviewUrl(URL.createObjectURL(file));
                                }}
                            />
                        </div>
                        {logoPreviewUrl && (
                            <div className="image-preview" style={{ marginTop: '15px', position: 'relative', display: 'inline-block' }}>
                                <img src={logoPreviewUrl} alt="Logo Preview" style={{ width: '140px', height: '140px', borderRadius: '15px', objectFit: 'cover', border: '2px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStoreLogo(null);
                                        setLogoPreviewUrl(null);
                                        const fileInput = document.getElementById("storeLogo");
                                        if (fileInput) fileInput.value = "";
                                    }}
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        background: '#C10000',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '24px',
                                        height: '24px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        zIndex: 10
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="Store-form-row">
                    <div className="Store-form-group">
                        <div className="Store-buttons">
                            <button className="add-Product-button" type="submit">
                                Add Store
                            </button>
                            <button className="cancel-Product-button" type="button" onClick={() => {
                                if (onCancel) onCancel();
                                else navigate('/stores');
                            }}>
                                cancel
                            </button>
                        </div>
                    </div>
                </div>

            </form>

            <PackageInvoiceModal
                isOpen={showInvoiceModal}
                onClose={() => {
                    setShowInvoiceModal(false);
                    if (onSuccess) onSuccess();
                    else navigate("/stores");
                }}
                invoiceData={createdInvoiceData}
                subscriptionId={createdSubId}
            />
        </div>
    );
};

export default AddStoreForm;